/**
 * Utilidad para cargar la API de Google Maps de manera asincrónica
 * Este archivo proporciona una forma centralizada y asincrónica de cargar Google Maps
 * para evitar problemas de rendimiento y advertencias de la consola.
 */

import { useState, useEffect } from 'react';

// Parámetros estándar para toda la aplicación
const DEFAULT_API_KEY = 'AIzaSyAQ1bUt3y6UoAhbi5-HAwYwaFx71pAiaKU';
const DEFAULT_LIBRARIES = ['places']; // Como array para @ubilabs/google-maps-react-hooks

// Exportar configuración estándar para uso con GoogleMapsProvider
export const GOOGLE_MAPS_CONFIG = {
  apiKey: DEFAULT_API_KEY,
  libraries: DEFAULT_LIBRARIES, // Debe ser array para @ubilabs/google-maps-react-hooks
  loading: 'async', // Añadir el parámetro loading=async para evitar advertencias
};

// Configuración para la carga del script de Google Maps
export const GOOGLE_MAPS_SCRIPT_CONFIG = {
  apiKey: DEFAULT_API_KEY,
  libraries: DEFAULT_LIBRARIES,
  loading: 'async', // Añadir el parámetro loading=async para evitar advertencias
};

/**
 * Hook para asegurar que los scripts de Google Maps se carguen correctamente
 * antes de inicializar componentes que dependan de ellos
 */
export const useGoogleMapsLoader = () => {
  const [isLoaded, setIsLoaded] = useState(!!window.google?.maps);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isLoaded && !error) {
      loadGoogleMapsAsync()
        .then(() => setIsLoaded(true))
        .catch((err) => setError(err));
    }
  }, [isLoaded, error]);

  return { isLoaded, error };
};

/**
 * Verifica si Google Maps está completamente cargado con todas las APIs necesarias
 */
const isGoogleMapsFullyLoaded = (): boolean => {
  return !!(
    window.google?.maps?.Map &&
    window.google?.maps?.Marker &&
    window.google?.maps?.Geocoder &&
    window.google?.maps?.places
  );
};

/**
 * Carga la API de Google Maps de manera asincrónica
 * @param apiKey - La clave de API de Google Maps
 * @param libraries - Bibliotecas opcionales para cargar (como array)
 * @returns Una promesa que se resuelve cuando la API se ha cargado correctamente
 */
export const loadGoogleMapsAsync = (
  apiKey: string = DEFAULT_API_KEY,
  libraries: string[] | string = DEFAULT_LIBRARIES,
): Promise<void> => {
  // Si Google Maps ya está cargado completamente, resolver inmediatamente
  if (isGoogleMapsFullyLoaded()) {
    return Promise.resolve();
  }

  // Eliminar scripts existentes para evitar conflictos
  const cleanupExistingScripts = () => {
    const oldScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    oldScripts.forEach((oldScript) => {
      oldScript.parentNode?.removeChild(oldScript);
    });
  };

  // Verificar si ya hay un script de Google Maps cargándose
  const existingScript = document.getElementById('google-maps-script');
  if (existingScript) {
    // Si ya hay un script cargándose, esperar a que termine
    return new Promise<void>((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 100; // 10 segundos máximo

      const checkGoogleMaps = setInterval(() => {
        attempts++;
        if (isGoogleMapsFullyLoaded()) {
          clearInterval(checkGoogleMaps);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkGoogleMaps);
          // Si después de muchos intentos no se carga, eliminamos el script existente y creamos uno nuevo
          cleanupExistingScripts();
          loadGoogleMapsAsync(apiKey, libraries).then(resolve).catch(reject);
        }
      }, 100);
    });
  }

  // Crear un nuevo script con carga asincrónica usando callback oficial
  return new Promise<void>((resolve, reject) => {
    try {
      // Limpiamos scripts existentes para evitar conflictos
      cleanupExistingScripts();

      // Registrar callback global ANTES de cargar el script
      const callbackName = 'initGoogleMaps';
      (window as unknown as Record<string, unknown>)[callbackName] = () => {
        // Verificar que todas las APIs estén disponibles
        if (isGoogleMapsFullyLoaded()) {
          resolve();
        } else {
          // Esperar un poco más si no están todas las APIs
          setTimeout(() => {
            if (isGoogleMapsFullyLoaded()) {
              resolve();
            } else {
              console.warn(
                'Google Maps API se cargó pero algunos componentes no están disponibles',
              );
              // Aún así resolvemos para evitar bloqueos
              resolve();
            }
          }, 500);
        }
      };

      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.type = 'text/javascript';

      // Convertir libraries a string si es un array
      const librariesParam = Array.isArray(libraries) ? libraries.join(',') : libraries;

      // Usar callback oficial de Google Maps para mejor control de carga
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${librariesParam}&callback=${callbackName}&loading=async`;
      script.async = true;
      script.defer = true;

      // Manejar errores de carga
      script.onerror = () => {
        delete (window as unknown as Record<string, unknown>)[callbackName];
        reject(new Error('Error al cargar Google Maps API'));
      };

      // Agregar el script al DOM
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Genera una URL para el mapa estático de Google Maps
 * @param latitude - Latitud del centro del mapa
 * @param longitude - Longitud del centro del mapa
 * @param zoom - Nivel de zoom (1-20)
 * @param width - Ancho de la imagen en píxeles
 * @param height - Alto de la imagen en píxeles
 * @param markerColor - Color del marcador (por ejemplo, "red", "blue")
 * @param apiKey - Clave de API de Google Maps (opcional, usa la predeterminada si no se proporciona)
 * @returns URL del mapa estático
 */
export const getStaticMapUrl = (
  latitude: number | string,
  longitude: number | string,
  zoom: number = 18,
  width: number = 400,
  height: number = 400,
  markerColor: string = 'red',
  apiKey: string = GOOGLE_MAPS_CONFIG.apiKey,
): string => {
  return `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=${zoom}&size=${width}x${height}&markers=color:${markerColor}%7Clabel:A%7C${latitude},${longitude}&key=${apiKey}`;
};

/**
 * Geocodifica una dirección para obtener sus coordenadas
 * @param address - La dirección a geocodificar
 * @returns Una promesa que resuelve a un objeto con lat y lng, o null si ocurre un error
 */
export const geocodeAddress = async (
  address: string,
): Promise<{ lat: string; lng: string } | null> => {
  // Si no hay dirección, no podemos geocodificar
  if (!address || address.trim() === '') {
    return null;
  }

  // Asegurarse de que la API de Google Maps esté cargada
  try {
    await loadGoogleMapsAsync();
  } catch (error) {
    console.error('Error al cargar Google Maps API para geocodificación:', error);
    return null;
  }

  // Verificar que el Geocoder esté disponible
  if (!window.google?.maps?.Geocoder) {
    console.error('Google Maps Geocoder no está disponible');
    return null;
  }

  // Crear una nueva instancia del geocoder
  const geocoder = new window.google.maps.Geocoder();

  // Retornar una promesa que se resolverá con las coordenadas
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results.length > 0 && results[0]?.geometry?.location) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat().toString(),
          lng: location.lng().toString(),
        });
      } else {
        console.warn(`Geocodificación fallida para "${address}". Status: ${status}`);
        // En caso de error, devolvemos null en lugar de rechazar la promesa
        // para facilitar el manejo de errores
        resolve(null);
      }
    });
  });
};
